import { getEffectiveApproachBeats, type CircleHitObject, type CirclesChart } from '@shared/circlesChart';

/**
 * Visual-only stacking for overlapping hit circles (osu! parity).
 *
 * Mappers sometimes place two or more circles at almost-identical
 * playfield positions in close beat-succession. Stock-rendered they
 * pile on top of each other and the player can't see the count;
 * osu! solves this by offsetting each subsequent circle slightly
 * up-and-left so they form a diagonal stack the eye can read.
 *
 * This is *purely visual*. The engine continues to read the raw
 * `obj.x` / `obj.y` for hit-detection and replays — only the
 * renderer (and `fx` spawn positions) apply the offset.
 *
 * # Algorithm (simplified osu!stable)
 *
 * For each circle (in beat order), walk backward looking for an
 * earlier circle that:
 *   1. is within `STACK_LENIENCY_BEATS_MUL * approachBeats` beats
 *      earlier, AND
 *   2. is within `STACK_OVERLAP_DISTANCE` osu units of the same
 *      position (basically "stacked right on top").
 *
 * If we find such an earlier circle, this one's stack depth =
 * earlier's depth + 1. Otherwise depth stays 0. The visual offset
 * is `depth * STACK_OFFSET_OSU` units in the (-x, -y) direction (up
 * and left), matching osu!'s default stack direction.
 *
 * # Why a WeakMap cache?
 *
 * The result is a pure function of `chart` so we memoise per chart
 * reference. Both the renderer and session call `getStackOffsets()`
 * frame after frame; the WeakMap means we compute once per
 * chart-instance and release with it (charts swap on game-mode
 * navigation).
 */

/**
 * Default stacking leniency: how many approach-windows of beat gap
 * still count as "visible at the same time" for stacking purposes.
 * Matches osu!stable's default of 0.7 — wider values stack circles
 * the mapper placed in distinct musical phrases, narrower ones miss
 * stacks the player can clearly see piling up.
 *
 * Charts can override via `chart.stackLeniency` (validated in
 * {@link import('@shared/circlesChart').validateCirclesChart}).
 */
const STACK_LENIENCY_BEATS_MUL_DEFAULT = 0.7;
// 6 osu units catches the "exactly stacked" case (osu!'s editor
// snaps to identical XY when mappers want to stack) plus tiny
// rounding drifts from the .osu→Circles import. Wider thresholds
// would visually merge circles the mapper placed close-but-distinct,
// which would read worse than the unstacked baseline.
const STACK_OVERLAP_DISTANCE = 6;
const STACK_OFFSET_OSU = 4;

export interface StackOffset {
  dx: number;
  dy: number;
}

const ZERO_OFFSET: StackOffset = { dx: 0, dy: 0 };

const stackCache: WeakMap<CirclesChart, ReadonlyMap<string, StackOffset>> = new WeakMap();

function computeStackOffsets(chart: CirclesChart): ReadonlyMap<string, StackOffset> {
  const result = new Map<string, StackOffset>();
  const hitObjects = chart.hitObjects;
  if (hitObjects.length === 0) {
    return result;
  }

  const sorted = [...hitObjects].sort((a, b) => a.beat - b.beat);
  const leniencyMul = chart.stackLeniency ?? STACK_LENIENCY_BEATS_MUL_DEFAULT;
  const stackThresholdBeats = leniencyMul * getEffectiveApproachBeats(chart);
  if (stackThresholdBeats <= 0) {
    return result;
  }
  const stackDepthById = new Map<string, number>();
  const overlapSq = STACK_OVERLAP_DISTANCE * STACK_OVERLAP_DISTANCE;

  for (let i = 1; i < sorted.length; i++) {
    const obj = sorted[i];
    if (obj.kind === 'spinner') {
      continue;
    }
    for (let j = i - 1; j >= 0; j--) {
      const prev = sorted[j];
      if (obj.beat - prev.beat > stackThresholdBeats) {
        break;
      }
      if (prev.kind === 'spinner') {
        continue;
      }
      const dx = obj.x - prev.x;
      const dy = obj.y - prev.y;
      if (dx * dx + dy * dy <= overlapSq) {
        const prevDepth = stackDepthById.get(prev.id) ?? 0;
        stackDepthById.set(obj.id, prevDepth + 1);
        break;
      }
    }
  }

  for (const [id, depth] of stackDepthById) {
    if (depth > 0) {
      result.set(id, { dx: -depth * STACK_OFFSET_OSU, dy: -depth * STACK_OFFSET_OSU });
    }
  }

  return result;
}

export function getStackOffsets(chart: CirclesChart): ReadonlyMap<string, StackOffset> {
  const cached = stackCache.get(chart);
  if (cached) {
    return cached;
  }
  const computed = computeStackOffsets(chart);
  stackCache.set(chart, computed);
  return computed;
}

/**
 * Convenience for callers that need the visual position of a
 * single hit object including stack offset. Spinners and circles
 * with no stack depth fall through to their raw coordinates.
 */
export function getStackedPosition(
  chart: CirclesChart,
  obj: CircleHitObject,
): { x: number; y: number } {
  const off = getStackOffsets(chart).get(obj.id) ?? ZERO_OFFSET;
  return { x: obj.x + off.dx, y: obj.y + off.dy };
}
